import * as React from 'react';
import { ArrowUp } from '../../primitives/Icon/icons';
import { Icon } from '../../primitives/Icon/Icon';
import {
  root as rootCls,
  textArea as textAreaCls,
  actionRow as actionRowCls,
  actionLeft as actionLeftCls,
  actionRight as actionRightCls,
  sendBtn as sendBtnCls,
  prominenceRoot,
  prominenceTextArea,
  prominenceSend,
} from './Composer.css';
import { MentionEditor } from './MentionEditor';
import type { ComposerMentions } from './mention';

export type { ComposerMentions, ComposerMentionItem } from './mention';

/**
 * How strongly the composer asserts itself as the primary input — a single
 * dial of increasing focal weight for surfaces where it would otherwise recede
 * into the panel (the Create-New-Workstream usability defect).
 *   - `flat`      — the calm baseline (bgSurface, faint placeholder). Default.
 *   - `raised`    — a white card lifted off the panel grey with a soft shadow.
 *   - `outlined`  — a crisp frame that flips to accent on focus; lightest touch.
 *   - `spotlight` — a white focal bar with an accent beacon frame + accent send.
 */
export type ComposerProminence = 'flat' | 'raised' | 'outlined' | 'spotlight';

export interface ComposerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Bound text value. Mentions are serialised inline as `${trigger}Label`. Defaults to `''`. */
  value?: string;
  /**
   * Fires on every edit with the new value. The Composer is **controlled**: pass
   * `onChange` whenever you pass `value`, and feed the value straight back
   * (don't normalise/trim it) — the editor reconciles against `value`, so a
   * value that doesn't round-trip unchanged makes the caret jump on every
   * keystroke.
   */
  onChange?: (value: string) => void;
  /** Fires when the user sends (button click, Enter, or Cmd/Ctrl+Enter). */
  onSend?: (value: string) => void;
  /** Placeholder shown while the editor is empty. */
  placeholder?: string;
  /** Optional left-side action buttons (paperclip, lightning, etc.). */
  leadingActions?: React.ReactNode;
  /**
   * Disables sending (controlled). When omitted, send is disabled iff the value
   * is empty / whitespace-only. Gates both the send button and Enter-to-send.
   */
  sendDisabled?: boolean;
  /** Aria-label for the send button. Defaults to "Send". */
  sendLabel?: string;
  /** Accessible name for the editor. Defaults to the placeholder (or "Message"). */
  ariaLabel?: string;
  /**
   * How strongly the composer reads as the primary input (see
   * {@link ComposerProminence}). Defaults to `flat` — the calm baseline. Raise
   * it on surfaces where the composer must clearly be the place to type.
   */
  prominence?: ComposerProminence;
  /**
   * Turn the composer into a `@mention`-style typeahead: supply the trigger
   * char, the items to show for a query (`getItems`), and an optional select
   * side effect. The editor renders committed mentions as real, atomic, styled
   * chips. See {@link ComposerMentions}. Omit to disable.
   */
  mentions?: ComposerMentions;
  /** Composer root ref. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Composer — chat input. A Lexical plain-text editor above an action row with
 * optional leading buttons and a filled-square send button.
 *
 * **Behaviour:**
 *   - Controlled via `value` + `onChange` (mentions serialise inline).
 *   - Enter (or Cmd/Ctrl+Enter) submits via `onSend(value)`.
 *   - Shift+Enter inserts a new line.
 *   - Send disabled when value is empty / whitespace-only (override via
 *     `sendDisabled`); a disabled send also blocks Enter-to-send.
 *   - The editor auto-grows then internal-scrolls (capped) so it never pushes
 *     the chat off screen.
 *   - With `mentions`, typing the trigger opens an agent typeahead; a committed
 *     mention is a real, atomic, styled `@Label` chip in the text.
 */
export function Composer({
  value = '',
  onChange,
  onSend,
  placeholder = 'Ask, instruct, or update the team…',
  leadingActions,
  sendDisabled,
  sendLabel = 'Send',
  ariaLabel,
  prominence = 'flat',
  mentions,
  ref,
  className,
  ...rest
}: ComposerProps) {
  const resolvedDisabled = sendDisabled ?? value.trim().length === 0;
  const accessibleName = ariaLabel ?? (placeholder.trim() || 'Message');
  const cls = [rootCls, prominenceRoot[prominence], className].filter(Boolean).join(' ');
  // The editor populates this with a send that reads its LIVE content, so the
  // button and Enter can't diverge on a stale value.
  const sendRef = React.useRef<() => void>(() => {});

  return (
    <div ref={ref} className={cls} {...rest}>
      <MentionEditor
        value={value}
        onChange={onChange ?? (() => {})}
        onSend={onSend ?? (() => {})}
        sendDisabled={sendDisabled}
        placeholder={placeholder}
        ariaLabel={accessibleName}
        editableClassName={[textAreaCls, prominenceTextArea[prominence]].join(' ')}
        mentions={mentions}
        sendRef={sendRef}
      />
      <div className={actionRowCls}>
        {leadingActions && <div className={actionLeftCls}>{leadingActions}</div>}
        <div className={actionRightCls}>
          <button
            type="button"
            className={[sendBtnCls, prominenceSend[prominence]].join(' ')}
            disabled={resolvedDisabled}
            aria-label={sendLabel}
            onClick={() => sendRef.current()}
          >
            <Icon as={ArrowUp} size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
Composer.displayName = 'Composer';
